import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Divider, IconButton, Tooltip, Pagination, InputAdornment
} from '@mui/material';
import {
  Globe, CheckCircle2, XCircle, Copy, Trash2, Server,
  ExternalLink, RefreshCw, Search
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getDisplayDomain } from '../utils/domainUtils';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const StepNumber = styled(Box)(({ active }) => ({
  width: 32, height: 32, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.85rem', fontWeight: 700,
  background: active ? '#7C3AED' : 'rgba(107,114,128,0.2)',
  color: active ? '#fff' : '#6B7280',
  flexShrink: 0,
}));

const InstructionBox = styled(Box)(() => ({
  background: 'rgba(124,58,237,0.06)',
  border: '1px solid rgba(124,58,237,0.15)',
  borderRadius: 12,
  padding: 20,
}));

const SITES_PER_PAGE = 5;

export default function ConfigurarDominio() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [domain, setDomain] = useState('');
  const [dnsStatus, setDnsStatus] = useState(null);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [activating, setActivating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const SERVER_IP = '187.127.21.250';

  const fetchSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSites(res.data.sites || []);
      }
    } catch (err) {
      setError('Erro ao carregar sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const selectedSite = sites.find(s => s._id === selectedSiteId);

  const filteredSites = useMemo(() => {
    if (!searchTerm.trim()) return sites;
    const term = searchTerm.toLowerCase();
    return sites.filter(s => {
      const name = (s.name || s.config?.DOMINIO || '').toLowerCase();
      const cd = (s.customDomain || '').toLowerCase();
      return name.includes(term) || cd.includes(term);
    });
  }, [sites, searchTerm]);

  const totalPages = Math.ceil(filteredSites.length / SITES_PER_PAGE);
  const paginatedSites = useMemo(() => {
    const start = (page - 1) * SITES_PER_PAGE;
    return filteredSites.slice(start, start + SITES_PER_PAGE);
  }, [filteredSites, page]);

  const handleCopyIP = () => {
    navigator.clipboard.writeText(SERVER_IP);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCheckDns = async () => {
    if (!domain.trim()) { setError('Informe o domínio'); return; }
    setError('');
    setDnsChecking(true);
    setDnsStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/${selectedSiteId}/check-dns`,
        { params: { domain: domain.trim() }, headers: { Authorization: `Bearer ${token}` } }
      );
      setDnsStatus(res.data);
    } catch (err) {
      setError('Erro ao verificar DNS');
    } finally {
      setDnsChecking(false);
    }
  };

  const handleActivate = async () => {
    setError('');
    setSuccess('');
    setActivating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/${selectedSiteId}/custom-domain`,
        { domain: domain.trim() },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      if (res.data.success) {
        setSuccess(`Domínio ativado! Seu site está disponível em ${res.data.url}`);
        setDomain('');
        setDnsStatus(null);
        fetchSites();
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao ativar domínio. Tente novamente.');
    } finally {
      setActivating(false);
    }
  };

  const handleRemove = async (siteId) => {
    if (!window.confirm('Tem certeza que deseja remover o domínio personalizado?')) return;
    setError('');
    setSuccess('');
    setRemoving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/sites/${siteId}/custom-domain`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setSuccess('Domínio personalizado removido com sucesso');
        fetchSites();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao remover domínio');
    } finally {
      setRemoving(false);
    }
  };

  const sitesWithDomain = sites.filter(s => s.customDomain);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Globe size={32} strokeWidth={1.75} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff' }}>Meu Domínio</Typography>
        </Box>
        <Typography sx={{ color: '#9CA3AF', mb: 4 }}>
          Configure seu próprio domínio para apontar para um site gerado no painel.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Domínios já configurados */}
        {sitesWithDomain.length > 0 && (
          <SectionCard>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={20} strokeWidth={1.75} color="#10B981" /> Domínios Ativos
            </Typography>
            {sitesWithDomain.map(site => (
              <Box key={site._id} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 2, borderRadius: 3, mb: 1,
                background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)',
                flexWrap: 'wrap', gap: 1
              }}>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>{site.customDomain}</Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>
                    Site: {site.name || site.config?.DOMINIO}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Abrir site">
                    <IconButton size="small" href={`https://${site.customDomain}`} target="_blank" sx={{ color: '#10B981' }}>
                      <ExternalLink size={18} strokeWidth={1.75} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover domínio">
                    <IconButton size="small" onClick={() => handleRemove(site._id)}
                      disabled={removing} sx={{ color: '#EF4444' }}>
                      <Trash2 size={18} strokeWidth={1.75} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </SectionCard>
        )}

        {/* ETAPA 1: Selecionar site */}
        <SectionCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <StepNumber active={!selectedSiteId}>1</StepNumber>
            <Typography variant="h6" sx={{ color: '#fff' }}>Selecionar Site</Typography>
          </Box>

          {sites.length === 0 ? (
            <Alert severity="info">Você ainda não tem sites criados. Crie um site primeiro.</Alert>
          ) : (
            <>
              <TextField
                fullWidth placeholder="Buscar site por nome ou domínio..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search size={20} strokeWidth={1.75} color="#6B7280" /></InputAdornment>
                  )
                }}
                size="small"
              />

              {paginatedSites.length === 0 ? (
                <Typography sx={{ color: '#6B7280', textAlign: 'center', py: 2 }}>
                  Nenhum site encontrado para "{searchTerm}"
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {paginatedSites.map(site => {
                    const siteName = site.name || site.config?.DOMINIO || '-';
                    const isSelected = site._id === selectedSiteId;
                    return (
                      <Box
                        key={site._id}
                        onClick={() => { setSelectedSiteId(site._id); setDnsStatus(null); setDomain(''); }}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          p: 2, borderRadius: 3, cursor: 'pointer', transition: 'all 0.2s',
                          background: isSelected ? 'rgba(124,58,237,0.12)' : 'rgba(255,255,255,0.02)',
                          border: isSelected ? '1px solid rgba(124,58,237,0.4)' : '1px solid rgba(255,255,255,0.05)',
                          '&:hover': { background: isSelected ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.05)', borderColor: 'rgba(124,58,237,0.25)' },
                          flexWrap: 'wrap', gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Globe size={20} strokeWidth={1.75} color={isSelected ? '#A78BFA' : '#6B7280'} style={{ flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontWeight: isSelected ? 600 : 400, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {siteName}
                            </Typography>
                            <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                              {getDisplayDomain(site)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {site.customDomain && (
                            <Chip size="small" label={site.customDomain} sx={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', fontSize: '0.7rem' }} />
                          )}
                          {isSelected && (
                            <Chip size="small" label="Selecionado" sx={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA', fontSize: '0.7rem', fontWeight: 600 }} />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages} page={page} onChange={(_, v) => setPage(v)}
                    size="small"
                    sx={{ '& .MuiPaginationItem-root': { color: '#9CA3AF' }, '& .Mui-selected': { background: 'rgba(124,58,237,0.2) !important', color: '#A78BFA' } }}
                  />
                </Box>
              )}

              <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', mt: 1, textAlign: 'right' }}>
                {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} encontrado{filteredSites.length !== 1 ? 's' : ''}
              </Typography>
            </>
          )}

          {selectedSite && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.05)' }}>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                Subdomínio atual: <strong style={{ color: '#A78BFA' }}>{getDisplayDomain(selectedSite)}</strong>
              </Typography>
              {selectedSite.customDomain && (
                <Typography sx={{ color: '#10B981', fontSize: '0.85rem', mt: 0.5 }}>
                  Domínio personalizado: <strong>{selectedSite.customDomain}</strong>
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>

        {/* ETAPA 2: Configurar DNS */}
        {selectedSiteId && (
          <Fade in timeout={300}>
            <SectionCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <StepNumber active={selectedSiteId && !dnsStatus?.pointing}>2</StepNumber>
                <Typography variant="h6" sx={{ color: '#fff' }}>Configurar DNS</Typography>
              </Box>

              <TextField
                fullWidth label="Seu domínio" placeholder="ex: meusite.com.br"
                value={domain}
                onChange={(e) => { setDomain(e.target.value.toLowerCase().trim()); setDnsStatus(null); }}
                sx={{ mb: 3 }}
                helperText="Digite o domínio sem http:// ou www."
              />

              <InstructionBox>
                <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Server size={18} strokeWidth={1.75} color="#7C3AED" />
                  Configure no seu registrador de domínio:
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', mb: 1 }}>
                    1. Acesse o painel DNS do seu registrador (GoDaddy, Hostinger, Registro.br, Cloudflare, etc.)
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', mb: 1 }}>
                    2. Crie um registro do tipo <strong style={{ color: '#A78BFA' }}>A</strong> com os seguintes dados:
                  </Typography>

                  <Box sx={{
                    display: 'flex', gap: 2, flexWrap: 'wrap', ml: 2, mt: 1, mb: 1.5,
                    p: 2, borderRadius: 2, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(124,58,237,0.1)'
                  }}>
                    <Box>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>Nome/Host</Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontFamily: 'monospace' }}>@</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>Tipo</Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontFamily: 'monospace' }}>A</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>Valor/Destino</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#F59E0B', fontWeight: 700, fontFamily: 'monospace', fontSize: '1.1rem' }}>{SERVER_IP}</Typography>
                        <Tooltip title={copied ? 'Copiado!' : 'Copiar IP'}>
                          <IconButton size="small" onClick={handleCopyIP} sx={{ color: copied ? '#10B981' : '#6B7280' }}>
                            <Copy size={18} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                    3. Aguarde a propagação DNS (pode levar até 24h, geralmente poucos minutos)
                  </Typography>
                </Box>
              </InstructionBox>

              <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined" onClick={handleCheckDns}
                  disabled={dnsChecking || !domain.trim()}
                  startIcon={dnsChecking ? <CircularProgress size={18} /> : <RefreshCw size={20} strokeWidth={1.75} />}
                  sx={{ borderRadius: 3 }}
                >
                  {dnsChecking ? 'Verificando...' : 'Verificar DNS'}
                </Button>

                {dnsStatus && (
                  <Chip
                    icon={dnsStatus.canActivate ? <CheckCircle2 size={20} strokeWidth={1.75} /> : <XCircle size={20} strokeWidth={1.75} />}
                    label={dnsStatus.canActivate ? 'DNS configurado corretamente!' : dnsStatus.pointing ? 'DNS aponta, mas há problemas' : `DNS não aponta para ${SERVER_IP}`}
                    sx={{
                      background: dnsStatus.canActivate ? 'rgba(16,185,129,0.15)' : dnsStatus.pointing ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                      color: dnsStatus.canActivate ? '#10B981' : dnsStatus.pointing ? '#F59E0B' : '#EF4444',
                      fontWeight: 600, '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                )}
              </Box>

              {dnsStatus && dnsStatus.addresses?.length > 0 && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: 'rgba(0,0,0,0.2)' }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', mb: 1 }}>
                    Registros A encontrados para <strong style={{ color: '#fff' }}>{domain}</strong>:
                  </Typography>
                  {dnsStatus.addresses.map((ip, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1, mb: 0.5 }}>
                      {ip === SERVER_IP ? (
                        <CheckCircle2 size={16} strokeWidth={1.75} color="#10B981" />
                      ) : (
                        <XCircle size={16} strokeWidth={1.75} color="#EF4444" />
                      )}
                      <Typography sx={{ color: ip === SERVER_IP ? '#10B981' : '#EF4444', fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 600 }}>
                        {ip}
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                        {ip === SERVER_IP ? '(correto)' : '(remover)'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {dnsStatus?.warnings?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {dnsStatus.warnings.map((w, i) => (
                    <Alert key={i} severity={dnsStatus.canActivate ? 'info' : dnsStatus.pointing ? 'warning' : 'error'} sx={{ mb: 1 }}>
                      {w}
                    </Alert>
                  ))}
                </Box>
              )}
            </SectionCard>
          </Fade>
        )}

        {/* ETAPA 3: Ativar */}
        {dnsStatus?.canActivate && (
          <Fade in timeout={300}>
            <SectionCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <StepNumber active>3</StepNumber>
                <Typography variant="h6" sx={{ color: '#fff' }}>Ativar Domínio</Typography>
              </Box>

              <Typography sx={{ color: '#9CA3AF', mb: 3 }}>
                O DNS está configurado corretamente. Clique no botão abaixo para ativar o domínio e gerar o certificado SSL.
                Este processo pode levar até 1 minuto.
              </Typography>

              <Divider sx={{ borderColor: 'rgba(124,58,237,0.1)', mb: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained" size="large" onClick={handleActivate}
                  disabled={activating}
                  startIcon={activating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Globe size={20} strokeWidth={1.75} />}
                  sx={{
                    px: 5, py: 1.5, borderRadius: 3, fontSize: '1rem',
                    background: '#10B981',
                    '&:hover': { background: '#059669' }
                  }}
                >
                  {activating ? 'Ativando domínio e gerando SSL...' : `Ativar ${domain.trim()}`}
                </Button>
              </Box>
            </SectionCard>
          </Fade>
        )}
      </Box>
    </Fade>
  );
}
